/**
 * Operations Reports API client
 *
 * Thin client for the daily operations report endpoints (#71).
 * Typed Axios wrapper around 5 backend endpoints (4 from Phase 2 + 1 from Phase 3 Task 1).
 * Mirrors the pattern of notificationApi.ts — one exported function per endpoint.
 */

import api from './api';
import type {
  ListRunsResponse,
  ListSubscriptionsResponse,
  DownloadUrlResponse,
  UpsertSubscriptionResponse,
} from '../types/operationsReports';

const BASE = '/v1/reports/operations';

export interface ListRunsParams {
  siteId?: string;
  from?: string;     // YYYY-MM-DD inclusive
  to?: string;       // YYYY-MM-DD inclusive
  limit?: number;    // default 30, max 200
}

export async function listRuns(params: ListRunsParams = {}): Promise<ListRunsResponse> {
  const search = new URLSearchParams();
  if (params.siteId) search.set('siteId', params.siteId);
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const qs = search.toString();
  const response = await api.get<ListRunsResponse>(`${BASE}/runs${qs ? `?${qs}` : ''}`);
  return response.data;
}

export async function fetchDownloadUrl(runId: string): Promise<DownloadUrlResponse> {
  const response = await api.get<DownloadUrlResponse>(`${BASE}/runs/${runId}/download-url`);
  return response.data;
}

export async function listSubscriptions(): Promise<ListSubscriptionsResponse> {
  const response = await api.get<ListSubscriptionsResponse>(`${BASE}/subscriptions`);
  return response.data;
}

export async function upsertSubscription(siteId: string, enabled: boolean): Promise<UpsertSubscriptionResponse> {
  const response = await api.put<UpsertSubscriptionResponse>(`${BASE}/subscriptions/${siteId}`, { enabled });
  return response.data;
}

// Manual re-trigger of a daily-ops generation for a specific site. Wraps the
// platform-admin-only POST /reports/operations/_internal/trigger endpoint.
// The frontend gates the retry affordance on the failed-row UI by role, but
// the backend enforces the same gate (#359 retry path).
export async function triggerRun(siteId: string): Promise<{ status: 'triggered' }> {
  const response = await api.post<{ status: 'triggered' }>(`${BASE}/_internal/trigger`, { siteId });
  return response.data;
}
